/**
 * The second step of signing in.
 *
 * Which factor is asked for is not this component's decision - the backend has
 * already made it and handed over a challenge. Rendering whichever arrives keeps
 * the rule in one place: a user with a passkey is never shown a code field, so
 * the weaker factor cannot be reached by anyone who can see this screen.
 */

import { AlertCircle, Fingerprint, Mail } from "lucide-react";
import React, { useCallback, useEffect, useRef, useState } from "react";

import {
  assertionFactor,
  type FactorChallenge,
} from "../../services/secondFactor";
import {
  isUserCancellation,
  passkeyService,
} from "../../services/passkey.service";

interface SecondFactorStepProps {
  challenge: FactorChallenge;
  /** Shown so the user knows which inbox to look in. */
  emailAddress: string;
  onSubmit: (factor: string, mfaToken?: string) => Promise<void>;
  onCancel: () => void;
  error: string;
  isSubmitting: boolean;
}

const CODE_LENGTH = 6;

const panel: React.CSSProperties = {
  background: "var(--ui-glass)",
  backdropFilter: "blur(20px)",
  borderRadius: "24px",
  padding: "32px",
  boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
  border: "1px solid rgba(255, 255, 255, 0.3)",
  marginBottom: "24px",
};

const primaryButton = (disabled: boolean): React.CSSProperties => ({
  width: "100%",
  height: "50px",
  background: "linear-gradient(45deg, #3b82f6, #22c55e)",
  color: "white",
  border: "none",
  borderRadius: "12px",
  fontSize: "16px",
  fontWeight: 600,
  cursor: disabled ? "not-allowed" : "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "8px",
  opacity: disabled ? 0.8 : 1,
});

export const SecondFactorStep: React.FC<SecondFactorStepProps> = ({
  challenge,
  emailAddress,
  onSubmit,
  onCancel,
  error,
  isSubmitting,
}) => {
  const [code, setCode] = useState("");
  const [passkeyError, setPasskeyError] = useState("");
  const codeInput = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (challenge.method === "email") codeInput.current?.focus();
  }, [challenge.method]);

  const runPasskey = useCallback(async () => {
    if (challenge.method !== "passkey") return;
    setPasskeyError("");
    try {
      const assertion = await passkeyService.sign(challenge.options);
      await onSubmit(assertionFactor(challenge.handle, assertion));
    } catch (err) {
      setPasskeyError(
        isUserCancellation(err)
          ? "Passkey prompt dismissed. Try again when you are ready."
          : "That passkey could not be used. Try again.",
      );
    }
  }, [challenge, onSubmit]);

  // The browser prompt is opened for the user rather than behind a second
  // click - by this point they have already asked to sign in.
  const started = useRef(false);
  useEffect(() => {
    if (challenge.method === "passkey" && !started.current) {
      started.current = true;
      void runPasskey();
    }
  }, [challenge.method, runPasskey]);

  const problem = error || passkeyError;

  return (
    <div style={panel} data-testid="second-factor-step">
      <div style={{ textAlign: "center", marginBottom: "24px" }}>
        {challenge.method === "email" ? (
          <Mail
            style={{
              width: 40,
              height: 40,
              color: "var(--ui-link)",
              margin: "0 auto",
            }}
          />
        ) : (
          <Fingerprint
            style={{
              width: 40,
              height: 40,
              color: "var(--ui-link)",
              margin: "0 auto",
            }}
          />
        )}
        <h2
          style={{
            fontSize: "22px",
            fontWeight: 700,
            color: "var(--ui-text-strong)",
            margin: "12px 0 8px 0",
          }}
        >
          {challenge.method === "email"
            ? "Enter your sign-in code"
            : "Confirm with your passkey"}
        </h2>
        <p
          style={{ color: "var(--ui-text-muted)", fontSize: "15px", margin: 0 }}
        >
          {challenge.method === "email"
            ? `We sent a ${CODE_LENGTH}-digit code to ${emailAddress}. It expires in 5 minutes.`
            : "Use Touch ID, Face ID, Windows Hello or your security key."}
        </p>
      </div>

      {problem && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            color: "var(--ui-error-fg)",
            background: "var(--ui-error-bg)",
            padding: "12px 16px",
            borderRadius: "12px",
            border: "1px solid var(--ui-error-line)",
            marginBottom: "16px",
          }}
          data-testid="second-factor-error"
        >
          <AlertCircle style={{ width: 20, height: 20, flexShrink: 0 }} />
          <span style={{ fontSize: "14px", fontWeight: 500 }}>{problem}</span>
        </div>
      )}

      {challenge.method === "email" ? (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void onSubmit(code, challenge.mfaToken);
          }}
          style={{ display: "flex", flexDirection: "column", gap: "16px" }}
        >
          <input
            ref={codeInput}
            id="factor-code"
            name="factor-code"
            type="text"
            inputMode="numeric"
            // Lets the browser and iOS offer the code straight from the message.
            autoComplete="one-time-code"
            maxLength={CODE_LENGTH}
            value={code}
            data-testid="factor-code-input"
            aria-label="Sign-in code"
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
            disabled={isSubmitting}
            style={{
              width: "100%",
              padding: "16px",
              border: "1px solid var(--ui-line)",
              borderRadius: "12px",
              fontSize: "28px",
              letterSpacing: "10px",
              textAlign: "center",
              outline: "none",
              boxSizing: "border-box",
            }}
          />
          <button
            type="submit"
            disabled={isSubmitting || code.length !== CODE_LENGTH}
            data-testid="factor-submit-button"
            style={primaryButton(isSubmitting || code.length !== CODE_LENGTH)}
          >
            {isSubmitting ? "Checking..." : "Continue"}
          </button>
        </form>
      ) : (
        <button
          type="button"
          onClick={() => void runPasskey()}
          disabled={isSubmitting}
          data-testid="factor-passkey-retry"
          style={primaryButton(isSubmitting)}
        >
          <Fingerprint style={{ width: 20, height: 20 }} />
          <span>{isSubmitting ? "Checking..." : "Use passkey"}</span>
        </button>
      )}

      <button
        type="button"
        onClick={onCancel}
        style={{
          width: "100%",
          marginTop: "16px",
          background: "none",
          border: "none",
          color: "var(--ui-text-muted)",
          fontSize: "14px",
          cursor: "pointer",
        }}
      >
        Start over
      </button>
    </div>
  );
};

export default SecondFactorStep;
